import React from 'react';
import {
  Pencil,
  OctagonX,
  Trash2,
  FolderPlus,
  Layers,
  Camera,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { ClaySwitch } from '../UI/ClaySwitch';
import { PERMISSION_LABELS } from '../../api/eventTeam';
import type { EventManagerPermissions } from '../../types';
import styles from './PermissionsForm.module.css';

interface PermissionsFormProps {
  value: EventManagerPermissions;
  onChange: (next: EventManagerPermissions) => void;
  disabled?: boolean;
}

interface PermissionItem {
  key: keyof EventManagerPermissions;
  icon: LucideIcon;
  description?: string;
  destructive?: boolean;
}

interface PermissionGroup {
  title: string;
  items: PermissionItem[];
}

const GROUPS: PermissionGroup[] = [
  {
    title: 'Event lifecycle',
    items: [
      { key: 'allowEditingEventDetails', icon: Pencil },
      { key: 'allowEndingEvent', icon: OctagonX },
      { key: 'allowDeletingEvent', icon: Trash2, description: 'Cannot be undone', destructive: true },
    ],
  },
  {
    title: 'Content',
    items: [
      { key: 'allowCreatingNewAlbum', icon: FolderPlus },
      { key: 'allowDeletingOfPhotos', icon: Trash2, destructive: true },
      { key: 'allowAddingNewPresets', icon: Layers },
    ],
  },
  {
    title: 'Access',
    items: [
      { key: 'allowAddingNewFtpAccount', icon: Camera },
      { key: 'allowManagingTeam', icon: Users, description: 'Can add or remove other managers' },
    ],
  },
];

export function PermissionsForm({ value, onChange, disabled }: PermissionsFormProps) {
  const set = (key: keyof EventManagerPermissions, next: boolean) => {
    onChange({ ...value, [key]: next });
  };

  const handleRowClick = (e: React.MouseEvent<HTMLLIElement>, key: keyof EventManagerPermissions) => {
    if (disabled) return;
    // Switch handles its own clicks; ignore them so we don't double-toggle.
    if ((e.target as HTMLElement).closest('[role="switch"]')) return;
    set(key, !value[key]);
  };

  return (
    <div className={styles['form']}>
      {GROUPS.map((group) => (
        <section key={group.title} className={styles['group']}>
          <h3 className={styles['group-title']}>{group.title}</h3>
          <ul className={styles['group-list']}>
            {group.items.map((item) => {
              const checked = value[item.key];
              const Icon = item.icon;
              const rowClass = [
                styles['row'],
                item.destructive && checked ? styles['row-destructive-active'] : '',
                disabled ? styles['row-disabled'] : '',
              ]
                .filter(Boolean)
                .join(' ');
              const iconClass = [
                styles['row-icon'],
                item.destructive ? styles['row-icon-destructive'] : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <li
                  key={item.key}
                  className={rowClass}
                  onClick={(e) => handleRowClick(e, item.key)}
                >
                  <span className={iconClass}>
                    <Icon className={styles['icon']} />
                  </span>
                  <div className={styles['row-text']}>
                    <span className={styles['row-label']}>{PERMISSION_LABELS[item.key]}</span>
                    {item.description && (
                      <span className={styles['row-description']}>{item.description}</span>
                    )}
                  </div>
                  <ClaySwitch
                    checked={checked}
                    onChange={(next) => set(item.key, next)}
                    disabled={disabled}
                  />
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
